import { ButtonGroup, ButtonV2, Dialog } from '@cimplico/components';
import { CheckIcon, CloudArrowUpIcon, DocumentDuplicateIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toastNotification } from '../../components/toast/Toast';
import { useUpdateCWTSchema } from '../../queries/use-update-cwt-schema';
import { CustomWorksheetTemplate } from '../../types/custom-worksheet-v2';
import { selectSchema } from './store/selectors';
import DuplicateForm from './CWTDuplicate';
import { useDeleteCWT } from '../../queries/use-delete-cwt';
import { useNavigate } from 'react-router-dom';
import { useUpdateCalculationTemplate } from '../../queries/resources/calculation-templates';

type PrimaryActionsProps = {
    template: CustomWorksheetTemplate;
};

function CWTPrimaryActions({ template }: PrimaryActionsProps): JSX.Element {
    const [action, setAction] = useState<string>();
    const { mutate, isLoading } = useUpdateCWTSchema(template.id);

    const schema = useSelector(selectSchema);

    function clearAction(): void {
        setAction(undefined);
    }

    function handleSave(): void {
        if (!schema) {
            return;
        }
        mutate(
            { schema: schema },
            {
                onSuccess: () => {
                    toastNotification('success', 'Your changes have been saved');
                },
                onError: () => {
                    toastNotification('error', 'Unable to save your changes');
                },
            }
        );
    }

    if (template.status === 'ARCHIVED') {
        return (
            <>
                {action === 'REMOVE' && <RemoveConfirm templateId={template.id} onClose={clearAction} />}
                <ButtonV2 type="danger" label="Permanently Delete" size="sm" onClick={(): void => setAction('REMOVE')} />
            </>
        );
    }

    return (
        <>
            {action === 'PUBLISH' && <PublishConfirm templateId={template.id} onClose={clearAction} />}
            {action === 'PUBLISH_NEW' && (
                <DuplicateForm
                    publish
                    template={template}
                    onClose={clearAction}
                    dialogTitle="Publish as new"
                    dialogDescription="Publishing will grant users access to this template inside current and newly created workpapers. Further changes to this template will need to be republished, requiring users to re-sync the template inside the worksheet."
                    successMessage="Successfully published template"
                    primaryAction="Publish"
                />
            )}
            <ButtonGroup
                size="sm"
                label="Save"
                leftIcon={<CheckIcon />}
                type="primary"
                onClick={handleSave}
                isLoading={isLoading}
                isDisabled={isLoading}
            >
                <ButtonGroup.SecondaryAction
                    label={template.status === 'PUBLISHED' ? 'Republish' : 'Publish'}
                    onClick={(): void => setAction('PUBLISH')}
                    leftIcon={<CloudArrowUpIcon />}
                />
                <ButtonGroup.SecondaryAction
                    label="Publish as new"
                    onClick={(): void => setAction('PUBLISH_NEW')}
                    leftIcon={<DocumentDuplicateIcon />}
                />
            </ButtonGroup>
        </>
    );
}

type ConfirmProps = {
    templateId: string;
    onClose: () => void;
};

function PublishConfirm({ templateId, onClose }: ConfirmProps): JSX.Element {
    const { mutate, isLoading } = useUpdateCalculationTemplate(templateId);

    function handlePublish(): void {
        mutate(
            { status: 'PUBLISHED' },
            {
                onError: (err) => {
                    toastNotification('error', err.response?.data.message ?? 'Something went wrong');
                },
                onSuccess: () => {
                    onClose();
                    toastNotification('success', 'Template published!');
                },
            }
        );
    }

    return (
        <Dialog
            title="Publish"
            primaryAction="Publish"
            secondaryAction="Cancel"
            description="Publishing will grant users access to this template inside current and newly created workpapers. Further changes to this template will need to be republished, requiring users to re-sync the template inside the worksheet."
            isOpen
            type="info"
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={handlePublish}
            isLoading={isLoading}
        />
    );
}

function RemoveConfirm({ templateId, onClose }: ConfirmProps): JSX.Element {
    const { isLoading, mutate } = useDeleteCWT();
    const navigate = useNavigate();

    function handleRemove(): void {
        mutate(
            { id: templateId },
            {
                onSuccess: () => {
                    toastNotification('success', 'Template removed');
                    navigate('..');
                },
                onError: () => {
                    toastNotification('error', 'Unable to remove template');
                },
            }
        );
    }

    return (
        <Dialog
            title="Permanently delete"
            primaryAction="Delete"
            secondaryAction="Cancel"
            description="This action is permanent. You will no longer have access to this template and its data. Any current worksheet using this template can still be completed."
            isOpen
            type="error"
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={handleRemove}
            isLoading={isLoading}
        />
    );
}

export default CWTPrimaryActions;
